import { ReactNode } from 'react';
import SimpleForm, { SimpleFormItemProps } from '../../SimpleForm';
import { EditColumnType } from '../interface';
import { dataToUploads, dataToUpload, getInitialValue } from '../utils';
import { apiTriggerType } from '../../SuperCascader/interface';

function getFormItem(record: any, tableElement: HTMLElement | null, props?: EditColumnType): ReactNode {
  if (!props) return null;
  const {
    formItemType,
    name,
    initialValue,
    required,
    rules,
    form,
    rowIndex,
    disabledRender,
    disabledRenderKey,
    enumCode,
    style,
    disabled,
    associationName,
    relativePathName,
    afterFormItemProps,
    inputProps,
    inputNumberProps,
    switchProps,
    checkboxProps,
    checkboxGroupProps,
    cascaderProps,
    datePickerProps,
    rangePickerProps,
    timePickerProps,
    selectProps,
    superSelectProps,
    selectTableProps,
    superSelectTableProps,
    uploadProps,
    uploadImageProps,
    uploadImagesProps,
    options,
    selectFirstValue,
    beforeFormItemFlex,
    afterFormItemFlex,
    middleText,
    beforeFormItemProps,
    emptyAfterValue,
    beforeFormItemWidth,
    formatRules,
  } = props;
  const relativePath = relativePathName
    ? record[relativePathName]
    : initialValue;
  const baseSearchFormProps: SimpleFormItemProps = {
    name,
    initialValue: getInitialValue(initialValue, record, name),
    form,
    rules: formatRules ? formatRules({ ...record, ...(form?.getFieldsValue() ?? {})}) : rules,
  };
  if (disabledRenderKey && !disabledRender) {
    baseSearchFormProps.disabledRender = record[disabledRenderKey];
  }
  if (required && !rules) {
    baseSearchFormProps.rules = [
      {
        required,
      },
    ];
  }
  const baseFormProps = { disabled: typeof disabled === 'function' ? disabled({ ...record, ...(form?.getFieldsValue() ?? {})}) : disabled};
  switch (formItemType) {
    case 'Input':
      return (
        <SimpleForm.Input
          {...baseSearchFormProps}
          inputProps={{ ...baseFormProps, ...inputProps }}
        />
      );
    case 'InputNumber':
      return (
        <SimpleForm.InputNumber
          {...baseSearchFormProps}
          inputNumberProps={{ ...baseFormProps, ...inputNumberProps }}
        />
      );
    case 'Switch':
      if (baseSearchFormProps.initialValue === undefined) {
        baseSearchFormProps.initialValue = false;
      }
      return (
        <SimpleForm.Switch
          {...baseSearchFormProps}
          switchProps={{ ...baseFormProps, ...switchProps }}
        />
      );
    case 'Checkbox':
      return (
        <SimpleForm.Checkbox
          {...baseSearchFormProps}
          checkboxProps={{ ...baseFormProps, ...checkboxProps }}
        />
      );
    case 'Cascader':
      let apiTrigger: apiTriggerType[] = [];
      const apiTriggerOut = cascaderProps?.apiTrigger;
      if (apiTriggerOut) {
        if (Array.isArray(apiTriggerOut)) {
          apiTrigger = apiTriggerOut;
        } else {
          apiTrigger = [apiTriggerOut];
        }
      };
      if (baseSearchFormProps.initialValue && !apiTrigger.includes('init')) {
        apiTrigger.push('init');
      }
      if (cascaderProps?.beforeFetch && !apiTrigger.includes('onFocus')) {
        apiTrigger.push('onFocus');
      }
      return (
        <SimpleForm.Cascader
          {...baseSearchFormProps}
          cascaderProps={{
            ...baseFormProps,
            ...cascaderProps,
            apiTrigger: apiTrigger.length ? apiTrigger : undefined,
            beforeFetch: cascaderProps?.beforeFetch ? (params: any, record?: any) => cascaderProps.beforeFetch?.(params, {...record, ...(form?.getFieldsValue() ?? {})}, rowIndex) : undefined,
            onChange: (value: any, item: any) => {
              associationName && form?.setFieldValue(associationName, undefined);
              cascaderProps?.onChange?.(value, item, form, rowIndex, {...record, ...(form?.getFieldsValue() ?? {})});
            },
          }}
        />
      );
    case 'CheckboxGroup':
      return (
        <SimpleForm.CheckboxGroup
          {...baseSearchFormProps}
          enumCode={enumCode}
          checkboxGroupProps={{ ...baseFormProps, ...checkboxGroupProps }}
        />
      );
    case 'DatePicker':
      return (
        <SimpleForm.DatePicker
          {...baseSearchFormProps}
          datePickerProps={{
            ...baseFormProps,
            ...datePickerProps,
          }}
        />
      );
    case 'RangePicker':
      return (
        <SimpleForm.RangePicker
          {...baseSearchFormProps}
          rangePickerProps={{
            ...baseFormProps,
            ...rangePickerProps,
          }}
        />
      );
    case 'TimePicker':
      return (
        <SimpleForm.TimePicker
          {...baseSearchFormProps}
          timePickerProps={{
            ...baseFormProps,
            ...timePickerProps,
          }}
        />
      );
    case 'Select':
      return (
        <SimpleForm.Select
          {...baseSearchFormProps}
          enumCode={enumCode}
          selectProps={{
            getPopupContainer: () => tableElement || document.body,
            ...baseFormProps,
            ...selectProps,
            onChange: (value: any, item: any) => {
              associationName && form?.setFieldValue(associationName, undefined);
              selectProps?.onChange?.(value, item);
            },
          }}
        />
      );
    case 'SuperSelect':
      return (
        <SimpleForm.SuperSelect
          {...baseSearchFormProps}
          enumCode={enumCode}
          superSelectProps={{
            getPopupContainer: () => tableElement || document.body,
            isDisabledEllipsis: true,
            disabledBorderDashed: false,
            ...baseFormProps,
            ...superSelectProps,
            onChange: (value: any, item: any) => {
              associationName && form?.setFieldValue(associationName, undefined);
              superSelectProps?.onChange?.(value, item, form, rowIndex, {...record, ...(form?.getFieldsValue() ?? {})},);
            },
          }}
        />
      );
    case 'SelectTable':
      if (!selectTableProps) return null;
      let initOptions: any[] = [];
      let namekey = props?.namekey;
      let valuekey = props?.valuekey || (Array.isArray(name) ? name[0] : name);
      let label = selectTableProps?.nameKey;
      let value = selectTableProps?.valueKey;
      if (namekey && valuekey && value && label) {
        const labels = record[namekey];
        const values = record[valuekey];
        if (labels !== undefined && values !== undefined) {
          if (Array.isArray(values)) {
            values.forEach((v, k) => {
              initOptions.push({
                [label as string]: labels[k],
                [value as string]: v,
              });
            });
          } else {
            initOptions.push({
              [label]: record[namekey],
              [value]: record[valuekey],
            });
          }
        }
      }
      return (
        <SimpleForm.SelectTable
          {...baseSearchFormProps}
          selectTableProps={{
            initOptions: initOptions.length ? initOptions : undefined,
            ...baseFormProps,
            ...selectTableProps,
            beforeFetch: selectTableProps.beforeFetch ? (params: any, record?: any) => selectTableProps.beforeFetch?.(params, {...record, ...(form?.getFieldsValue() ?? {})}, rowIndex) : undefined,
            onChange: (value: any, item: any) => {
              associationName && form?.setFieldValue(associationName, undefined);
              selectTableProps?.onChange?.(value, item, form, rowIndex, {...record, ...(form?.getFieldsValue() ?? {})});
            },
            getPopupContainer: () => tableElement || document.body,
          }}
        />
      );
    case 'SuperSelectTable':
      if (!superSelectTableProps) return null;
      let initOptions_1: any[] = [];
      let labelKey_1 = props?.labelKey;
      let valueKey_1 = props?.valueKey || (Array.isArray(name) ? name[0] : name);
      let label_1 = superSelectTableProps?.labelKey;
      let value_1 = superSelectTableProps?.valueKey;
      if (labelKey_1 && valueKey_1 && label_1 && value_1) {
        const labels = record[labelKey_1];
        const values = record[valueKey_1];
        if (labels !== undefined && values !== undefined) {
          if (Array.isArray(values)) {
            values.forEach((v, k) => {
              initOptions_1.push({
                [label_1 as string]: labels[k],
                [value_1 as string]: v,
              });
            });
          } else {
            initOptions_1.push({
              [label_1]: record[labelKey_1],
              [value_1]: record[valueKey_1],
            });
          }
        }
      }
      const labelValue = (Array.isArray(name) && name.length === 2) ? record?.[name[1]] : undefined;
      return (
        <SimpleForm.SuperSelectTable
          {...baseSearchFormProps}
          labelValue={labelValue}
          superSelectTableProps={{
            disabledBorderDashed: false,
            initOptions: initOptions_1.length ? initOptions_1 : undefined,
            ...baseFormProps,
            ...superSelectTableProps,
            beforeFetch: superSelectTableProps.beforeFetch ? (params: any, record?: any) => superSelectTableProps.beforeFetch?.(params, {...record, ...(form?.getFieldsValue() ?? {})}, rowIndex) : undefined,
            isOpenUpdate: superSelectTableProps.isOpenUpdate || !!superSelectTableProps.beforeFetch,
            onChange: (value: any, item: any) => {
              associationName && form?.setFieldValue(associationName, undefined);
              superSelectTableProps?.onChange?.(value, item, form, rowIndex, {...record, ...(form?.getFieldsValue() ?? {})});
            },
            getPopupContainer: () => tableElement || document.body,
          }}
        />
      );
    case 'SelectCustom':
      let beforeName: string = '';
      let afterName: string = '';
      if (Array.isArray(name)) {
        beforeName = name[0] as string;
        afterName = name[1] as string;
      }
      return (
        <SimpleForm.SelectCustom
          {...baseSearchFormProps}
          disabled={disabled}
          options={options}
          selectFirstValue={selectFirstValue}
          beforeFormItemFlex={beforeFormItemFlex}
          afterFormItemFlex={afterFormItemFlex}
          middleText={middleText}
          beforeFormItemProps={{
            ...beforeFormItemProps,
            initialValue: getInitialValue(undefined, record, beforeName),
          }}
          afterFormItemProps={{
            ...afterFormItemProps,
            initialValue: getInitialValue(undefined, record, afterName),
          }}
          emptyAfterValue={emptyAfterValue}
          beforeFormItemWidth={beforeFormItemWidth}
        />
      );
    case 'Upload':
      return (
        <SimpleForm.Upload
          {...baseSearchFormProps}
          initialValue={dataToUploads(initialValue, relativePath)}
          uploadProps={{
            getPopupContainer: () => tableElement || document.body,
            ...baseFormProps,
            ...uploadProps,
          }}
        />
      );
    case 'UploadImage':
      return (
        <SimpleForm.UploadImage
          {...baseSearchFormProps}
          initialValue={dataToUpload(initialValue, relativePath)}
          style={style}
          uploadImageProps={{
            getPopupContainer: () => tableElement || document.body,
            ...baseFormProps,
            ...uploadImageProps,
          }}
        />
      );
    case 'UploadImages':
      return (
        <SimpleForm.UploadImages
          {...baseSearchFormProps}
          initialValue={dataToUploads(initialValue, relativePath)}
          uploadImagesProps={{
            getPopupContainer: () => tableElement || document.body,
            ...baseFormProps,
            ...uploadImagesProps,
          }}
        />
      );
    default:
      return (
        <SimpleForm.Input
          {...baseSearchFormProps}
          inputProps={{ ...baseFormProps, ...inputProps }}
        />
      );
  }
}

export default getFormItem;
